import axios from "axios";

import { setChildMaxAgeYears } from "./childMaxAge";
import { setModifyBookingEnabled } from "./modifyBooking";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ChainBranding {
	primaryColor?: string;
	accentColor?: string;
	primaryColorDark?: string;
	accentColorDark?: string;
	/** When true, dark palette may apply when the OS prefers dark and dark colors are set. */
	darkBrandingEnabled?: boolean;
	logoUrl?: string;
	logoDarkUrl?: string;
	faviconUrl?: string;
	newsletterBrand?: string;
	confirmUrl?: string;
	/** Optional page background (light theme). Hex. Falls back to warm off-white. */
	backgroundColorLight?: string;
	/** Optional page background (dark theme). Hex. Falls back to near-black blue-gray. */
	backgroundColorDark?: string;
	/**
	 * Optional full HTML for the privacy policy page. When present, the client renders this
	 * instead of the translated default (chain-specific legal text, trademarks, DPO contacts).
	 */
	privacyPolicyHtml?: string | null;
}

/**
 * Which Opera LOV feeds a given country-or-nationality form field. Backend `LovSource` enum.
 * {@code NATIONALITY} → typically 3-letter codes; {@code COUNTRYCODES} → typically 2-letter codes.
 * The actual format is whatever the property has configured the LOV with in Opera.
 */
export type LovSource = "NATIONALITY" | "COUNTRYCODES";

/**
 * Per-chain override telling the SPA which LOV map to read for each of the three
 * country-or-nationality form fields. Always present in V14+ responses; the SPA treats a
 * missing block as today's defaults so a mid-rollout state (BE deployed, SPA upgraded to
 * read the block before BE has it) is safe.
 *
 * Mirrors the backend's {@code HotelsInfoController.LovSources} record.
 */
export interface LovSources {
	nationality: LovSource;
	birthCountry: LovSource;
	addressCountry: LovSource;
}

/**
 * Response of {@code GET /oci/api/v1/chain-info?hostname=...}. The backend resolves the chain by
 * hostname; the specific hotel within the chain is only known after the booking is fetched from
 * Opera, so this payload deliberately does NOT include a hotel id.
 *
 * <p>{@code chainId} is the stable UUID the SPA pins for every subsequent API call header and
 * for matching the JWT chain claim; {@code chainCode} is a renameable display label kept only
 * for diagnostics.
 */
export interface ChainInfo {
	chainId: string;
	chainCode: string;
	chainName: string;
	hostname: string | null;
	operaType: "OWS" | "OHIP";
	branding: ChainBranding | null;
	/** Optional for forward/backward safety; if absent the SPA uses LOV_SOURCE_DEFAULTS. */
	lovSources?: LovSources;
	/**
	 * Inclusive max full years at arrival for CHILD slots; null disables age rules.
	 * Omitted on legacy backend responses — treated as 17.
	 */
	childMaxAgeYears?: number | null;
	/** When false, guest ETA (ModifyBooking) is disabled. Omitted = enabled. */
	modifyBookingEnabled?: boolean;
}

export interface TenantConfig {
	chainId: string;
	chainCode: string;
	chainName: string;
	branding: ChainBranding;
	/**
	 * Optional. When absent (legacy backend response or feature not yet shipped server-side),
	 * downstream consumers should use {@code LOV_SOURCE_DEFAULTS} from {@code guestUtils}.
	 */
	lovSources?: LovSources;
	/**
	 * null = age rules disabled; undefined until tenant loads — use {@link getChildMaxAgeYears}.
	 */
	childMaxAgeYears?: number | null;
	/** When false, guest ETA (ModifyBooking) is disabled. Omitted = enabled. */
	modifyBookingEnabled?: boolean;
}

export type TenantError = "CHAIN_NOT_FOUND" | "NETWORK_ERROR";

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let currentTenant: TenantConfig | null = null;
let tenantError: TenantError | null = null;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Sends the current hostname to the backend. The backend looks up the matching chain and returns
 * its branding + chainId + chainCode in a single response. The chainId is what subsequent API
 * calls send in the {@code chainId} header so the backend can resolve OWS/OHIP configuration
 * via a stable identifier. The specific hotel within the chain is discovered later, from the
 * OWS FetchBooking response.
 */
export async function initializeTenant(): Promise<TenantConfig> {
	const url = resolveChainInfoUrl();
	const hostname = window.location.hostname.toLowerCase();

	try {
		const { data } = await axios.get<ChainInfo>(url, {
			params: { hostname },
			timeout: 10_000,
		});

		currentTenant = {
			chainId: data.chainId,
			chainCode: data.chainCode,
			chainName: data.chainName,
			branding: data.branding ?? {},
			// Kept optional so legacy backend responses (no lovSources field) flow through to
			// the consumer's default-resolution path instead of being forced into a shape here.
			lovSources: data.lovSources,
			childMaxAgeYears: data.childMaxAgeYears,
			modifyBookingEnabled: data.modifyBookingEnabled,
		};
		setChildMaxAgeYears(data.childMaxAgeYears);
		setModifyBookingEnabled(data.modifyBookingEnabled);

		tenantError = null;
		return currentTenant;
	} catch (err: any) {
		if (err?.response?.status === 404) {
			tenantError = "CHAIN_NOT_FOUND";
		} else {
			tenantError = "NETWORK_ERROR";
		}
		console.error("Failed to initialize tenant:", err);
		throw err;
	}
}

export function getTenantConfig(): TenantConfig | null {
	return currentTenant;
}

export function getTenantError(): TenantError | null {
	return tenantError;
}

export function isTenantResolved(): boolean {
	return currentTenant !== null;
}

export function getChainCode(): string {
	if (!currentTenant) {
		throw new Error("Tenant not initialized. Call initializeTenant() first.");
	}
	return currentTenant.chainCode;
}

/**
 * Stable chain UUID -- the source of truth for the {@code chainId} header on every guest API
 * call. Prefer this over {@link getChainCode} for anything other than display / logging:
 * admins can rename the chainCode from the dashboard, and a renamed code would silently
 * detach an in-flight session from its chain.
 */
export function getChainId(): string {
	if (!currentTenant) {
		throw new Error("Tenant not initialized. Call initializeTenant() first.");
	}
	return currentTenant.chainId;
}

export { getChildMaxAgeYears } from "./childMaxAge";
export { isModifyBookingEnabled } from "./modifyBooking";

// ---------------------------------------------------------------------------
// Internal
// ---------------------------------------------------------------------------

/** Spring server.servlet.context-path (must match online-checkin's application.yaml; default /online-checkin). */
export function apiContextPath(): string {
	return (process.env.REACT_APP_API_CONTEXT_PATH || "/online-checkin").replace(/\/$/, "");
}

function resolveChainInfoUrl(): string {
	const pathSuffix = "/oci/api/v1/chain-info";
	if (
		process.env.NODE_ENV === "development" &&
		process.env.REACT_APP_API_BASE_URL
	) {
		try {
			const u = new URL(process.env.REACT_APP_API_BASE_URL);
			return `${u.origin}${apiContextPath()}${pathSuffix}`;
		} catch {
			// fall through
		}
	}
	return `${apiContextPath()}${pathSuffix}`;
}
